import { ThemePalette } from 'mui.theme';
import {
  ResponsiveContainer,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  Line,
} from 'recharts';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { type HourlyAssetSensortCategoryDistribution } from './types';
import dayjs from 'dayjs';
import CustomTooltip from './Components/Tooltip';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface paramsInterface {
  sensors: HourlyAssetSensortCategoryDistribution[];
  epoch_seconds: number;
}

interface AssetProfileChartProps {
  asset: string;
  groupedMovingAverage: any[];
  setSensorsInRedCategory?: (params: paramsInterface) => void;
  enableDarkTheme: boolean;
  onPointClick: (
    sensors: Array<{ sensor: string; redsPercentage: string; ts: string }>
  ) => void;
  onClearSensors?: () => void;
}
const AssetProfileChart = ({
  groupedMovingAverage,
  asset,
  setSensorsInRedCategory,
  enableDarkTheme,
  onPointClick,
  onClearSensors,
}: AssetProfileChartProps) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<'default' | 'individual'>('default');
  const [showActualValue, setShowActualValue] = useState(true);
  const [showMovingAverage, setShowMovingAverage] = useState(true);
  const [individualSensorToggles, setIndividualSensorToggles] = useState<
    Record<string, { actual: boolean; movingAverage: boolean }>
  >({});

  const handleViewModeChange = (newViewMode: 'default' | 'individual') => {
    setViewMode(newViewMode);
    if (onClearSensors) {
      onClearSensors();
    }
  };

  const handleLegendClick = (data: any) => {
    if (viewMode === 'default') {
      if (data.value === 'Actual Value') {
        setShowActualValue(!showActualValue);
      } else if (data.value === 'Moving Average') {
        setShowMovingAverage(!showMovingAverage);
      }
    } else if (viewMode === 'individual') {
      if (data.type === 'actual') {
        setIndividualSensorToggles((prev) => ({
          ...prev,
          [data.value]: {
            ...prev[data.value],
            actual: !prev[data.value]?.actual,
          },
        }));
      } else if (data.type === 'avg') {
        setIndividualSensorToggles((prev) => ({
          ...prev,
          [data.value]: {
            ...prev[data.value],
            movingAverage: !prev[data.value]?.movingAverage,
          },
        }));
      }
    }
  };

  const chartData = useMemo(() => {
    if (!groupedMovingAverage || !Array.isArray(groupedMovingAverage)) {
      return [];
    }

    if (viewMode === 'individual') {
      // Individual sensor view: group by timestamp and create columns for each sensor
      const tsMap = new Map();
      const allSensors = new Set();

      groupedMovingAverage.forEach((item) => {
        const sensorName =
          item.device_sensor.S.split('-')[1]?.replace(/_/g, ' ') || 'Unknown';
        allSensors.add(sensorName);
      });

      const sensorNames = Array.from(allSensors) as string[];
      const newToggles: Record<
        string,
        { actual: boolean; movingAverage: boolean }
      > = {};
      sensorNames.forEach((sensorName) => {
        if (!individualSensorToggles[sensorName]) {
          newToggles[sensorName] = { actual: true, movingAverage: true };
        } else {
          newToggles[sensorName] = individualSensorToggles[sensorName];
        }
      });
      if (Object.keys(newToggles).length > 0) {
        setIndividualSensorToggles(newToggles);
      }

      groupedMovingAverage.forEach((item) => {
        const ts = item.ts.N;
        const sensorName =
          item.device_sensor.S.split('-')[1]?.replace(/_/g, ' ') || 'Unknown';
        const redsPercentage = parseFloat(item.reds_percentage.N) / 100;
        const avgVal = parseFloat(item.avg_val.N);

        if (!tsMap.has(ts)) {
          const dataPoint: any = {
            ts,
            formattedTime: dayjs(parseInt(ts)).format('HH:mm'),
            fullTimestamp: dayjs(parseInt(ts)).format('YYYY-MM-DD HH:mm:ss'),
          };

          allSensors.forEach((sensor) => {
            dataPoint[sensor as string] = null;
            dataPoint[`${sensor}_avg`] = null;
          });

          tsMap.set(ts, dataPoint);
        }

        tsMap.get(ts)[sensorName] = redsPercentage;
        tsMap.get(ts)[`${sensorName}_avg`] = avgVal;
      });

      return Array.from(tsMap.values()).sort(
        (a, b) => parseInt(a.ts) - parseInt(b.ts)
      );
    }

    const tsMap = new Map();

    groupedMovingAverage.forEach((item) => {
      const ts = item.ts.N;
      const redsPercentage = parseFloat(item.reds_percentage.N);
      const avgVal = parseFloat(item.avg_val.N);

      if (!tsMap.has(ts)) {
        tsMap.set(ts, {
          ts,
          redsPercentages: [],
          avgVals: [],
          formattedTime: dayjs(parseInt(ts)).format('HH:mm'),
          fullTimestamp: dayjs(parseInt(ts)).format('YYYY-MM-DD HH:mm:ss'),
          device_sensor: item.device_sensor.S,
        });
      }

      tsMap.get(ts).redsPercentages.push(redsPercentage);
      tsMap.get(ts).avgVals.push(avgVal);
    });

    const result = Array.from(tsMap.values()).map((item) => {
      const totalRedsPercentage = item.redsPercentages.reduce(
        (sum: number, percentage: number) => sum + percentage,
        0
      );
      const maxPossiblePercentage = item.redsPercentages.length * 100;
      const yAxisValue = totalRedsPercentage / maxPossiblePercentage;

      const totalAvgVal = item.avgVals.reduce(
        (sum: number, avgVal: number) => sum + avgVal,
        0
      );
      const meanAvgVal = totalAvgVal / item.avgVals.length;

      return {
        ts: item.ts,
        y_axis_value: yAxisValue,
        avg_val: meanAvgVal,
        formattedTime: item.formattedTime,
        fullTimestamp: item.fullTimestamp,
        device_sensor: item.device_sensor,
        reds_percentage: item.redsPercentages.join(','),
      };
    });

    return result.sort((a, b) => parseInt(a.ts) - parseInt(b.ts));
  }, [groupedMovingAverage, viewMode]);

  // Fixed Y-axis domain from 0 to 1
  const minValue = 0;
  const maxValue = 1;

  if (!chartData || chartData.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 300,
          backgroundColor: enableDarkTheme
            ? palette.boxBackground
            : palette.boxBackground,
          borderRadius: '8px',
          border: enableDarkTheme
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography
          sx={{
            color: enableDarkTheme
              ? 'rgba(255, 255, 255, 0.7)'
              : 'rgba(0, 0, 0, 0.7)',
            fontSize: '16px',
          }}
        >
          {t('no_data_available_for_visualization')}
        </Typography>
      </Box>
    );
  }

  const handlePointClick = (data: any) => {
    const sensors = groupedMovingAverage
      ?.filter((item) => item.ts.N === data.ts)
      .map((item) => {
        return {
          sensor: item.device_sensor.S.split('-')[1].replace(/_/g, ' '),
          redsPercentage: item.reds_percentage.N,
          ts: item.ts.N,
        };
      });
    onPointClick(sensors);
  };

  return (
    <Box
      sx={{
        width: '95%',
        height: 300,
        backgroundColor: enableDarkTheme
          ? palette.boxBackground
          : palette.boxBackground,
        borderRadius: '8px',
        border: enableDarkTheme
          ? '1px solid rgba(255, 255, 255, 0.1)'
          : '1px solid rgba(0, 0, 0, 0.1)',
        padding: '20px 10px 20px 10px',
      }}
    >
      {/* Legend and Controls */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'top',
          marginBottom: '12px',
        }}
      >
        {/* Legend */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '2px',
            alignItems: 'center',
            flex: 1,
            marginRight: '16px',
          }}
        >
          {viewMode === 'default' ? (
            <>
              <Box
                onClick={() => {
                  handleLegendClick({ value: 'Actual Value' });
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  padding: '4px',
                  color: showActualValue
                    ? enableDarkTheme
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.05)'
                    : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    color: 'rgba(203, 85, 85, 0.3)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: '16px',
                    height: '2px',
                    backgroundColor: '#2196F3',
                    opacity: showActualValue ? 1 : 0.3,
                    transition: 'opacity 0.2s ease',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '10px',
                    color: enableDarkTheme
                      ? showActualValue
                        ? 'rgba(255, 255, 255, 0.9)'
                        : 'rgba(255, 255, 255, 0.4)'
                      : showActualValue
                      ? 'rgba(0, 0, 0, 0.9)'
                      : 'rgba(0, 0, 0, 0.4)',
                    fontWeight: showActualValue ? 600 : 400,
                    transition: 'all 0.2s ease',
                  }}
                >
                  {t('actual_value')}
                </Typography>
              </Box>
              <Box
                onClick={() => {
                  handleLegendClick({ value: 'Moving Average' });
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  padding: '4px',
                  color: showMovingAverage
                    ? enableDarkTheme
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.05)'
                    : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    color: 'rgba(203, 85, 85, 0.3)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: '16px',
                    height: '2px',
                    backgroundColor: '#F44336',
                    opacity: showMovingAverage ? 1 : 0.3,
                    transition: 'opacity 0.2s ease',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '10px',
                    color: enableDarkTheme
                      ? showMovingAverage
                        ? 'rgba(255, 255, 255, 0.9)'
                        : 'rgba(255, 255, 255, 0.4)'
                      : showMovingAverage
                      ? 'rgba(0, 0, 0, 0.9)'
                      : 'rgba(0, 0, 0, 0.4)',
                    fontWeight: showMovingAverage ? 600 : 400,
                    transition: 'all 0.2s ease',
                  }}
                >
                  {t('moving_average')}
                </Typography>
              </Box>
            </>
          ) : (
            // Individual sensor legend
            chartData.length > 0 &&
            Object.keys(individualSensorToggles).map((sensorName) => {
              const sensorIndex = Object.keys(individualSensorToggles).indexOf(
                sensorName
              );
              const colors = [
                '#2196F3',
                '#F44336',
                '#4CAF50',
                '#FF9800',
                '#9C27B0',
                '#00BCD4',
                '#795548',
                '#607D8B',
                '#E91E63',
                '#3F51B5',
              ];
              const baseColor = colors[sensorIndex % colors.length];
              const toggleState = individualSensorToggles[sensorName];

              return (
                <React.Fragment key={sensorName}>
                  <Box
                    onClick={() => {
                      handleLegendClick({
                        value: `${sensorName}`,
                        type: 'actual',
                      });
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: 'pointer',
                      padding: '4px',
                      color: toggleState?.actual
                        ? enableDarkTheme
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'rgba(0, 0, 0, 0.05)'
                        : 'transparent',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: 'rgba(203, 85, 85, 0.3)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '4px',
                        height: '2px',
                        backgroundColor: baseColor,
                        opacity: toggleState?.actual ? 1 : 0.3,
                        transition: 'opacity 0.2s ease',
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '10px',
                        color: enableDarkTheme
                          ? toggleState?.actual
                            ? 'rgba(255, 255, 255, 0.9)'
                            : 'rgba(255, 255, 255, 0.4)'
                          : toggleState?.actual
                          ? 'rgba(0, 0, 0, 0.9)'
                          : 'rgba(0, 0, 0, 0.4)',
                        fontWeight: toggleState?.actual ? 600 : 400,
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {sensorName}
                    </Typography>
                  </Box>
                  <Box
                    onClick={() => {
                      handleLegendClick({
                        value: `${sensorName}`,
                        type: 'avg',
                      });
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: 'pointer',
                      padding: '4px',
                      color: toggleState?.movingAverage
                        ? enableDarkTheme
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'rgba(0, 0, 0, 0.05)'
                        : 'transparent',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: 'rgba(203, 85, 85, 0.3)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '4px',
                        height: '2px',
                        backgroundColor: baseColor,
                        opacity: toggleState?.movingAverage ? 1 : 0.3,
                        transition: 'opacity 0.2s ease',
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '10px',
                        color: baseColor,
                        opacity: toggleState?.movingAverage ? 1 : 0.3,
                        fontWeight: toggleState?.movingAverage ? 600 : 400,
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {sensorName}
                    </Typography>
                  </Box>
                </React.Fragment>
              );
            })
          )}
        </Box>

        {/* Dropdown for view mode selection */}
        <Box>
          <FormControl
            size="small"
            sx={{
              minWidth: 160,
              '& .MuiOutlinedInput-root': {
                height: '32px',
                fontSize: '12px',
                color: enableDarkTheme
                  ? 'rgba(255, 255, 255, 0.9)'
                  : 'rgba(0, 0, 0, 0.8)',
                backgroundColor: enableDarkTheme
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.02)',
                '& fieldset': {
                  borderColor: enableDarkTheme
                    ? 'rgba(255, 255, 255, 0.2)'
                    : 'rgba(0, 0, 0, 0.2)',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: enableDarkTheme
                    ? 'rgba(255, 255, 255, 0.4)'
                    : 'rgba(0, 0, 0, 0.4)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: enableDarkTheme
                    ? 'rgba(33, 150, 243, 0.8)'
                    : 'rgba(33, 150, 243, 0.6)',
                  borderWidth: '1px',
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '11px',
                transform: 'translate(14px, 8px) scale(1)',
                '&.MuiInputLabel-shrink': {
                  transform: 'translate(14px, -6px) scale(0.85)',
                },
              },
              '& .MuiSelect-select': {
                padding: '6px 14px',
                fontSize: '12px',
              },
            }}
          >
            <InputLabel
              sx={{
                color: enableDarkTheme
                  ? 'rgba(255, 255, 255, 0.6)'
                  : 'rgba(0, 0, 0, 0.6)',
                fontSize: '11px',
              }}
            >
              {t('view')}
            </InputLabel>
            <Select
              value={viewMode}
              onChange={(e) => {
                handleViewModeChange(
                  e.target.value as 'default' | 'individual'
                );
              }}
              label={t('view')}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: enableDarkTheme
                      ? 'rgba(30, 30, 30, 0.95)'
                      : 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    border: enableDarkTheme
                      ? '1px solid rgba(255, 255, 255, 0.1)'
                      : '1px solid rgba(0, 0, 0, 0.1)',
                    boxShadow: enableDarkTheme
                      ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                      : '0 8px 32px rgba(0, 0, 0, 0.1)',
                  },
                },
              }}
            >
              <MenuItem
                value="default"
                sx={{
                  fontSize: '12px',
                  padding: '6px 16px',
                  color: enableDarkTheme
                    ? 'rgba(255, 255, 255, 0.9)'
                    : 'rgba(0, 0, 0, 0.8)',
                  '&:hover': {
                    backgroundColor: enableDarkTheme
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                {t('default_view')}
              </MenuItem>
              <MenuItem
                value="individual"
                sx={{
                  fontSize: '12px',
                  padding: '6px 16px',
                  color: enableDarkTheme
                    ? 'rgba(255, 255, 255, 0.9)'
                    : 'rgba(0, 0, 0, 0.8)',
                  '&:hover': {
                    backgroundColor: enableDarkTheme
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                {t('individual_sensors')}
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <ResponsiveContainer width="100%" height="90%">
        <LineChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 5, bottom: 0 }}
          onClick={(e) => {
            handlePointClick(e.activePayload?.[0]?.payload);
          }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={
              enableDarkTheme
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.1)'
            }
            vertical={false}
            horizontal={false}
          />

          <ReferenceArea y1={0.66} y2={1.0} fill="#FF6B6B" fillOpacity={0.15} />
          <ReferenceArea
            y1={0.33}
            y2={0.66}
            fill="#6BCF7F"
            fillOpacity={0.15}
          />
          <ReferenceArea y1={0} y2={0.33} fill="#FFD93D" fillOpacity={0.15} />

          <XAxis
            dataKey="formattedTime"
            tick={{
              fontSize: 12,
              fill: enableDarkTheme
                ? 'rgba(255, 255, 255, 0.7)'
                : 'rgba(0, 0, 0, 0.7)',
            }}
            axisLine={{
              stroke: enableDarkTheme
                ? 'rgba(255, 255, 255, 0.3)'
                : 'rgba(0, 0, 0, 0.3)',
            }}
            angle={-45}
            textAnchor="end"
            height={60}
          />

          <YAxis
            domain={[minValue, maxValue]}
            tick={{
              fontSize: 12,
              fill: enableDarkTheme
                ? 'rgba(255, 255, 255, 0.7)'
                : 'rgba(0, 0, 0, 0.7)',
            }}
            axisLine={{
              stroke: enableDarkTheme
                ? 'rgba(255, 255, 255, 0.3)'
                : 'rgba(0, 0, 0, 0.3)',
            }}
          />

          <Tooltip
            content={
              <CustomTooltip
                groupedMovingAverage={groupedMovingAverage}
                viewMode={viewMode}
                individualSensorToggles={individualSensorToggles}
              />
            }
          />

          {viewMode === 'default' ? (
            <>
              {showActualValue && (
                <Line
                  type="monotone"
                  dataKey="y_axis_value"
                  stroke="#2196F3"
                  strokeWidth={2}
                  name="Actual Value"
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              )}

              {showMovingAverage && (
                <Line
                  type="monotone"
                  dataKey="avg_val"
                  stroke="#F44336"
                  strokeWidth={2}
                  name="Moving Average"
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              )}
            </>
          ) : (
            (() => {
              if (chartData.length === 0) return null;

              // Get all unique sensor names from the first data point (excluding _avg suffixed ones)
              const sensorNames = Object.keys(chartData[0]).filter(
                (key) =>
                  key !== 'ts' &&
                  key !== 'formattedTime' &&
                  key !== 'fullTimestamp' &&
                  !key.endsWith('_avg')
              );

              const colors = [
                '#2196F3',
                '#F44336',
                '#4CAF50',
                '#FF9800',
                '#9C27B0',
                '#00BCD4',
                '#795548',
                '#607D8B',
                '#E91E63',
                '#3F51B5',
              ];

              const lines: JSX.Element[] = [];

              sensorNames.forEach((sensorName, index) => {
                const baseColor = colors[index % colors.length];
                const toggleState = individualSensorToggles[sensorName];

                // Add actual value line only if enabled
                if (toggleState?.actual) {
                  lines.push(
                    <Line
                      key={`${sensorName}_actual`}
                      type="monotone"
                      dataKey={sensorName}
                      stroke={baseColor}
                      strokeWidth={2}
                      name={`${sensorName} (Actual)`}
                      connectNulls={false}
                      strokeDasharray="0"
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  );
                }

                // Add moving average line only if enabled
                if (toggleState?.movingAverage) {
                  lines.push(
                    <Line
                      key={`${sensorName}_avg`}
                      type="monotone"
                      dataKey={`${sensorName}_avg`}
                      stroke={baseColor}
                      strokeWidth={2}
                      name={`${sensorName} (Avg)`}
                      connectNulls={false}
                      strokeDasharray="5 5"
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  );
                }
              });

              return lines;
            })()
          )}

          <defs>
            <linearGradient id="blueGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#2196F3" stopOpacity={0.6} />
              <stop offset="95%" stopColor="#2196F3" stopOpacity={0.1} />
            </linearGradient>
            <linearGradient id="redGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#F44336" stopOpacity={0.6} />
              <stop offset="95%" stopColor="#F44336" stopOpacity={0.1} />
            </linearGradient>
          </defs>
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default AssetProfileChart;
